import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BookOutlined, CopyOutlined, DeleteOutlined, SaveOutlined, SendOutlined } from "@ant-design/icons";
import { Alert, Popconfirm, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { Button, Form } from "../../../../shared";
import FeatureFlagGuard from "../../../../FeatureFlagGuard/FeatureFlagGuard";
import { usePamFeature } from "../../../../../hooks/useFeatureFlag";
import { SIDER_TABS } from "../../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../../utils/NavigationUtils";
import { useDynamicBonusCatalog } from "../../../../../queries/dynamicBonuses/useDynamicBonusCatalog";
import { useDynamicBonusEvents } from "../../../../../queries/dynamicBonuses/useDynamicBonusEvents";
import { useCreateCampaign } from "../../../../../queries/dynamicBonuses/useCreateCampaign";
import { useCreateTemplate } from "../../../../../queries/dynamicBonuses/useCreateTemplate";
import {
  useCampaignTransition,
  type CampaignTransitionAction,
} from "../../../../../queries/dynamicBonuses/useCampaignTransition";
import { useCreateSeries } from "../../../../../queries/dynamicBonuses/useCreateSeries";
import { AppStrings } from "../../../../../utils/constants/AppStrings";
import { formatString } from "../../../../../utils/constants/stringUtils";
import { PERCENT_OF_TRIGGER_PARAM_KEY, RESULT_PARAM_SCHEMA } from "../../constants";
import { siblingConditionMatches } from "../../resultParamRules";
import { BANNER_DEEPLINK_VALIDATION_FIELDS } from "../../controls/bannerUtils";
import CampaignBuilderHeader from "../../controls/CampaignBuilderHeader";
import { displayNameFor } from "../../eventCatalog";
import StartStep from "./StartStep";
import TriggerStepsStep from "./TriggerStepsStep";
import DisplayBannerStep from "./DisplayBannerStep";
import ActivateCampaignReviewModal from "./ActivateCampaignReviewModal";
import SendTestModal from "../SendTestModal";
import {
  buildCampaignBody,
  buildSeriesBody,
  buildTemplateBody,
  buildTemplateIn,
  defaultTriggerModeFor,
  triggerEventKeyFor,
  DEFAULT_SCHEDULE_MODE,
  DEFAULT_CAMPAIGN_TYPE,
  DISPLAY_BANNER_CAMPAIGN_TYPE,
  DISPLAY_BANNER_RESULT_TYPE_KEY,
  SERIES_CAMPAIGN_TYPE,
  defaultWizardCampaignType,
  type WizardCampaignType,
  type WizardFormValues,
  type WizardStepFormValue,
  type WizardTriggerMode,
} from "./toWizardCampaignIn";
import {
  AWARD_FREQUENCY,
  DEFAULT_CUSTOM_FREQUENCY_AMOUNT,
  DEFAULT_CUSTOM_FREQUENCY_UNIT,
  isInformationalBannerOnly,
  shouldShowAwardFrequency,
} from "./awardFrequency";
import type {
  CampaignIn,
  DynamicBonusCatalog,
  DynamicBonusCampaignStatus,
  ResultType,
  SeriesIn,
  TemplateIn,
  BannerDeeplinkIn,
} from "../../types";
import wizardStyles from "../../../../Affiliates/NewAffiliate/NewAffiliate.module.css";
import styles from "../../stylesDynamicBonuses.module.css";

const EMPTY_STEPS: WizardStepFormValue[] = [];

type AdvanceField = string | (string | number)[];

/** Fields that must validate before draft / activate / template / save. */
const stepsAdvanceFields = (
  triggerMode: WizardTriggerMode,
  steps: WizardStepFormValue[],
  catalog: DynamicBonusCatalog | undefined,
  affiliateReferralCode: string | undefined,
): AdvanceField[] => {
  const fields: AdvanceField[] = [];
  if (triggerMode === "event") fields.push(["steps", 0, "event_key"]);
  if (triggerMode === "affiliate_referral") {
    // affiliate_id isn't on CampaignOut, so edit hydrate leaves it undefined.
    // Only require it when no referral code is present yet (create / re-pick).
    if (!affiliateReferralCode) {
      fields.push("affiliate_id");
    }
    fields.push("affiliate_referral_code");
  }
  steps.forEach((_, index) => {
    if (triggerMode !== "none") {
      fields.push(["steps", index, "result_type_key"]);
    }
    const resultTypeKey = steps[index]?.result_type_key;
    const resultType = catalog?.results?.find(
      (result) => result.type_key === resultTypeKey,
    )?.result_type;
    // OFFER banners and the campaign_start step-1 award receipt require a
    // title (BONENG-849). Other award steps show the designer optionally.
    const isAwardOnCampaignStartStepOne =
      index === 0 &&
      triggerMode === "campaign_start" &&
      resultType === "award";
    if (resultTypeKey === DISPLAY_BANNER_RESULT_TYPE_KEY) {
      fields.push(["steps", index, "display_locations"]);
      fields.push(["steps", index, "banner", "image", "url"]);
      const claimVisible = steps[index]?.banner?.claim?.visible !== false;
      if (claimVisible) {
        fields.push(["steps", index, "banner", "claim", "text", "en"]);
      }
      const displayBannerDeeplink = steps[index]?.banner?.deeplink;
      if (displayBannerDeeplink && typeof displayBannerDeeplink !== "string") {
        (BANNER_DEEPLINK_VALIDATION_FIELDS[displayBannerDeeplink.screen] ?? []).forEach(
          (field) => {
            fields.push(["steps", index, "banner", "deeplink", field]);
          },
        );
      }
    } else if (resultType === "offer" || isAwardOnCampaignStartStepOne) {
      fields.push(["steps", index, "banner", "title", "text", "en"]);
      const deeplink = steps[index]?.banner?.deeplink;
      if (deeplink && typeof deeplink !== "string") {
        (BANNER_DEEPLINK_VALIDATION_FIELDS[deeplink.screen] ?? []).forEach(
          (field) => {
            fields.push(["steps", index, "banner", "deeplink", field]);
          },
        );
      }
    }
    if (index > 0 && triggerMode !== "none") {
      fields.push(["steps", index, "event_key"]);
    }
    // Result params with rules must gate submit: required params (e.g. amount)
    // because fields can be unmounted (BONENG-902), requiredWhen siblings
    // (ThreeOaks value), bounded params (split_count 1..25, BONENG-883), and
    // format validators (show_bonus american_odds, BONENG-896) because the
    // backend 422s invalid values.
    const resultParams = steps[index]?.result_params as
      | Record<string, unknown>
      | undefined;
    const percentRaw = resultParams?.[PERCENT_OF_TRIGGER_PARAM_KEY];
    const percentSizing =
      typeof percentRaw === "string"
        ? percentRaw.trim().length > 0
        : percentRaw !== undefined && percentRaw !== null && percentRaw !== "";
    (RESULT_PARAM_SCHEMA[resultTypeKey ?? ""] ?? [])
      .filter((param) => {
        // Percent mode unmounts the fixed-$ field; validating it here would
        // surface "Amount is required" on a field the operator cannot see.
        if (percentSizing && param.replacedByPercent) return false;
        return (
          param.required ||
          siblingConditionMatches(param.requiredWhen, resultParams) ||
          param.min !== undefined ||
          param.max !== undefined ||
          param.format !== undefined
        );
      })
      .forEach((param) => {
        fields.push(["steps", index, "result_params", param.key]);
      });
  });
  return fields;
};

const fieldHasValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

/** Terminal statuses where a test send is meaningless. */
const TEST_SEND_BLOCKED_STATUSES = new Set<DynamicBonusCampaignStatus>([
  "expired",
  "archived",
]);

type TransitionButton = {
  action: CampaignTransitionAction;
  label: string;
  danger?: boolean;
};

const campaignTransitions = (): Record<string, TransitionButton[]> => ({
  draft: [
    {
      action: "activate",
      label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_ACTIVATE,
    },
  ],
  active: [
    {
      action: "pause",
      label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_PAUSE,
    },
    {
      action: "expire",
      label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_EXPIRE,
      danger: true,
    },
  ],
  paused: [
    {
      action: "activate",
      label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_ACTIVATE,
    },
    {
      action: "expire",
      label: AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_EXPIRE,
      danger: true,
    },
  ],
  expired: [],
  archived: [],
});

/** Props shared by both wizard modes. */
type SharedProps = {
  /** Page heading (e.g. "Create Template" / "Edit Campaign"). */
  title?: string;
  /** Submit button label override. */
  submitLabel?: string;
  /** Prefill for edit / template flows (applied as form initial values). */
  initialValues?: Partial<WizardFormValues>;
  /** Keeps the wizard shell visible while route prefill loads. */
  isInitialValuesLoading?: boolean;
  /** Blocks an unintentional scratch campaign when prefill fails. */
  initialValuesError?: string;
  /** Pending state of an injected submit mutation. */
  isSubmitting?: boolean;
  /**
   * Series-stamped run (BONENG-842) — form is disabled, save / transitions /
   * Send Test are hidden. Optional `seriesId` sends Back to the series detail.
   */
  readOnly?: boolean;
  /** Active campaigns allow banner edits while bonus rules remain locked. */
  rulesLocked?: boolean;
  seriesId?: string;
  /**
   * Existing Braze series (edit) or a stamped child run. Selects Braze,
   * disables both source pills (Priority-style), and hides audience.
   */
  lockBrazeSource?: boolean;
  /** Opens a clone-preview create flow. Hidden when omitted (flag off / create). */
  onDuplicate?: () => void;
};

/** Campaign/series create — internal mutation. Edit — host owns PUT via onSubmit. */
type CampaignModeProps = SharedProps & {
  mode?: "campaign";
  onSubmit?: (body: CampaignIn | SeriesIn) => void;
  /** When set, shows status transitions + Send Test in the wizard header. */
  editCampaign?: {
    campaignId: string;
    status: DynamicBonusCampaignStatus;
  };
  onDelete?: () => void;
};

/** Template mode — the host owns the create/edit mutation. */
type TemplateModeProps = SharedProps & {
  mode: "template";
  onSubmit: (template: TemplateIn) => void;
  editCampaign?: never;
  /** When set, shows Delete in the wizard header (edit flow). */
  onDelete?: () => void;
};

type Props = CampaignModeProps | TemplateModeProps;

const CreateCampaignWizard: React.FC<Props> = ({
  mode = "campaign",
  title,
  submitLabel,
  initialValues,
  isInitialValuesLoading = false,
  initialValuesError,
  onSubmit,
  isSubmitting: isSubmittingProp = false,
  editCampaign,
  readOnly = false,
  rulesLocked = false,
  seriesId,
  lockBrazeSource = false,
  onDelete,
  onDuplicate,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<WizardFormValues>();
  const [sendTestOpen, setSendTestOpen] = useState(false);
  const [draftCampaign, setDraftCampaign] = useState<CampaignIn | null>(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const isTemplateMode = mode === "template";
  const isEditMode = Boolean(editCampaign?.campaignId) && !readOnly;
  const isViewMode = Boolean(editCampaign?.campaignId) && readOnly;

  const { data: catalog, isLoading: catalogLoading } = useDynamicBonusCatalog();
  const { data: eventGroups, isLoading: eventsLoading } = useDynamicBonusEvents();
  const { isEnabled: seriesEnabled } = usePamFeature("dynamicBonusesExternal");
  const { isEnabled: draftTestSendEnabled } = usePamFeature(
    "draftCampaignTestSend",
  );
  const { isEnabled: dynamicBonusesBetaMode } = usePamFeature(
    "dynamicBonusesBetaMode",
  );
  const { isEnabled: displayBannerV2Flag } = usePamFeature("displayBannerV2");
  const displayBannerV2 = displayBannerV2Flag === true;
  // Templates are always PAM-shaped — never default to Braze/series, even when
  // dynamicBonusesExternal is on. Series result locks would otherwise leave
  // the Result picker at show_bonus-only while Campaign type is hidden.
  const defaultCampaignType = isTemplateMode
    ? DEFAULT_CAMPAIGN_TYPE
    : defaultWizardCampaignType(Boolean(seriesEnabled));
  const initialCampaignType =
    initialValues?.campaign_type ?? defaultCampaignType;
  const { mutate: createCampaign, isPending: campaignPending } = useCreateCampaign();
  const { mutate: createTemplate, isPending: templatePending } = useCreateTemplate();
  const transitionMutation = useCampaignTransition(editCampaign?.campaignId ?? "");
  const { mutate: createSeries, isPending: seriesPending } = useCreateSeries();

  // `preserve: true` keeps the watchers live while a field's step is unmounted
  // — the form itself preserves values, but useWatch ignores unregistered
  // fields by default.
  const watch = { form, preserve: true } as const;
  const watchedCampaignTypeField = Form.useWatch("campaign_type", watch) as
    | WizardCampaignType
    | undefined;
  const watchedCampaignType = watchedCampaignTypeField ?? defaultCampaignType;
  const isSeries = watchedCampaignType === SERIES_CAMPAIGN_TYPE;
  const isDisplayBanner =
    watchedCampaignType === DISPLAY_BANNER_CAMPAIGN_TYPE && !displayBannerV2;
  const brazeSourceLocked = lockBrazeSource || Boolean(seriesId);
  const watchedAudienceMode = Form.useWatch("audience_mode", watch) as
    | "all"
    | "segment"
    | undefined;
  const watchedTriggerMode = Form.useWatch("trigger_mode", watch) as
    | WizardTriggerMode
    | undefined;
  const watchedSteps =
    (Form.useWatch("steps", watch) as WizardStepFormValue[] | undefined) ??
    EMPTY_STEPS;
  const showAwardFrequency = shouldShowAwardFrequency({
    isDisplayBanner:
      isDisplayBanner || isInformationalBannerOnly(watchedSteps),
    isBraze: isSeries || brazeSourceLocked,
    betaMode: dynamicBonusesBetaMode,
  });
  const watchedAwardFrequency = Form.useWatch("award_frequency", watch);
  const watchedAffiliateReferralCode = Form.useWatch(
    "affiliate_referral_code",
    watch,
  ) as string | undefined;
  const watchedAffiliateId = Form.useWatch("affiliate_id", watch) as
    | string
    | undefined;

  useEffect(() => {
    if (!displayBannerV2) return;
    if (watchedCampaignTypeField !== DISPLAY_BANNER_CAMPAIGN_TYPE) return;
    form.setFieldValue("campaign_type", DEFAULT_CAMPAIGN_TYPE);
  }, [displayBannerV2, form, watchedCampaignTypeField]);

  const resolveResultType = useCallback(
    (typeKey: string): ResultType | "" =>
      catalog?.results?.find((r) => r.type_key === typeKey)?.result_type ?? "",
    [catalog],
  );

  const detailsValidationFields = useCallback((): AdvanceField[] => {
    const fields: AdvanceField[] = isTemplateMode
      ? ["name"]
      : isSeries
        ? ["name"]
        : ["name", "date_range"];
    if (!isSeries && !isDisplayBanner && watchedAudienceMode === "segment") {
      fields.push("segment_id");
    }
    if (
      showAwardFrequency &&
      watchedAwardFrequency === AWARD_FREQUENCY.CUSTOM
    ) {
      fields.push("award_frequency_amount");
    }
    if (
      showAwardFrequency &&
      watchedAwardFrequency !== AWARD_FREQUENCY.ONE_TIME
    ) {
      fields.push("max_awards_per_user");
    }
    if (isDisplayBanner) {
      fields.push(["steps", 0, "display_locations"]);
      fields.push(["steps", 0, "banner", "image", "url"]);
      const claimVisible =
        form.getFieldValue(["steps", 0, "banner", "claim", "visible"]) !==
        false;
      if (claimVisible) {
        fields.push(["steps", 0, "banner", "claim", "text", "en"]);
      }
      // Banner itself can open a deeplink even when the CTA button is hidden.
      const deeplink = form.getFieldValue([
        "steps",
        0,
        "banner",
        "deeplink",
      ]) as BannerDeeplinkIn | string | undefined;
      if (deeplink && typeof deeplink !== "string") {
        (BANNER_DEEPLINK_VALIDATION_FIELDS[deeplink.screen] ?? []).forEach(
          (field) => {
            fields.push(["steps", 0, "banner", "deeplink", field]);
          },
        );
      }
      return fields;
    }
    const triggerMode: WizardTriggerMode =
      form.getFieldValue("trigger_mode") ?? "none";
    const steps: WizardStepFormValue[] = form.getFieldValue("steps") ?? [];
    fields.push(
      ...stepsAdvanceFields(
        triggerMode,
        steps,
        catalog,
        form.getFieldValue("affiliate_referral_code"),
      ),
    );
    return fields;
  }, [catalog, form, isDisplayBanner, isSeries, isTemplateMode, showAwardFrequency, watchedAudienceMode, watchedAwardFrequency]);

  const validateThen = useCallback(
    (action: () => void) => {
      form
        .validateFields(detailsValidationFields())
        .then(action)
        .catch(() => {});
    },
    [detailsValidationFields, form],
  );

  /**
   * Header "Save as Draft" — same validation as Activate / Create.
   *
   * Campaigns: POST without auto-activate, then return to the campaigns list.
   * Series: intentionally excluded from that return-to-list flow. Series create
   * already stays draft (no activate step); reuse the existing create-series
   * success path (series detail + Braze payload dialog) so operators still get
   * the Braze JSON they need after save.
   */
  const handleSaveAsDraft = useCallback(() => {
    if (readOnly || isEditMode || isTemplateMode) return;

    validateThen(() => {
      const allValues = form.getFieldsValue(true) as WizardFormValues;
      const goToCampaigns = () =>
        navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS);

      if (allValues.campaign_type === SERIES_CAMPAIGN_TYPE) {
        const series = buildSeriesBody(allValues, resolveResultType);
        createSeries(series, {
          onSuccess: (created) =>
            navigate(
              NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_DETAIL.replace(
                ":seriesId",
                created.body.series_campaign_id,
              ),
              { state: { openBrazePayload: true } },
            ),
        });
        return;
      }

      const campaign = buildCampaignBody(allValues, resolveResultType);
      createCampaign(
        { campaign, activate: false },
        { onSuccess: goToCampaigns },
      );
    });
  }, [
    createCampaign,
    createSeries,
    form,
    isEditMode,
    isTemplateMode,
    navigate,
    readOnly,
    resolveResultType,
    validateThen,
  ]);

  /** Header "Save as Template" — template only; does not create a campaign. */
  const handleSaveAsTemplate = useCallback(() => {
    if (readOnly || isEditMode || isTemplateMode || isSeries) return;

    validateThen(() => {
      const allValues = form.getFieldsValue(true) as WizardFormValues;
      const campaign = buildCampaignBody(allValues, resolveResultType);
      const templateName = `${campaign.name} template`;
      createTemplate(buildTemplateBody(templateName, campaign), {
        onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES),
      });
    });
  }, [
    createTemplate,
    form,
    isEditMode,
    isSeries,
    isTemplateMode,
    navigate,
    readOnly,
    resolveResultType,
    validateThen,
  ]);

  const onFinish = useCallback(
    (values: WizardFormValues) => {
      if (readOnly) return;

      const allValues = {
        ...form.getFieldsValue(true),
        ...values,
      } as WizardFormValues;

      if (isTemplateMode) {
        (onSubmit as (template: TemplateIn) => void)?.(
          buildTemplateIn(allValues, resolveResultType),
        );
        return;
      }

      // A series is config-only until an explicit Activate on its detail
      // page — do NOT reuse useCreateCampaign, which auto-activates
      // immediately after POST. Edit hosts own the PUT via onSubmit.
      if (allValues.campaign_type === SERIES_CAMPAIGN_TYPE) {
        const series = buildSeriesBody(allValues, resolveResultType);
        if (onSubmit) {
          (onSubmit as (body: CampaignIn | SeriesIn) => void)(series);
          return;
        }
        createSeries(series, {
          onSuccess: (created) =>
            navigate(
              NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_DETAIL.replace(
                ":seriesId",
                created.body.series_campaign_id,
              ),
              { state: { openBrazePayload: true } },
            ),
        });
        return;
      }

      const campaign = buildCampaignBody(allValues, resolveResultType);

      if (onSubmit) {
        (onSubmit as (body: CampaignIn | SeriesIn) => void)(campaign);
        return;
      }

      createCampaign(campaign, {
        onSuccess: () => navigate(NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS),
      });
    },
    [
      createCampaign,
      createSeries,
      form,
      isTemplateMode,
      navigate,
      onSubmit,
      readOnly,
      resolveResultType,
    ],
  );

  /**
   * Validate then submit — Create Series / Save / Create Template run immediately.
   * Campaign-create Activate opens the review modal first (BONENG-1025).
   */
  const handlePrimarySubmit = useCallback(() => {
    if (readOnly) return;
    const isCampaignCreateReview =
      !isEditMode && !isTemplateMode && !onSubmit && !isSeries;
    if (isCampaignCreateReview) {
      validateThen(() => setIsReviewOpen(true));
      return;
    }
    validateThen(() => {
      onFinish(form.getFieldsValue(true) as WizardFormValues);
    });
  }, [
    form,
    isEditMode,
    isSeries,
    isTemplateMode,
    onFinish,
    onSubmit,
    readOnly,
    validateThen,
  ]);

  const handleReviewSaveAsDraft = useCallback(() => {
    setIsReviewOpen(false);
    handleSaveAsDraft();
  }, [handleSaveAsDraft]);

  const handleReviewActivate = useCallback(() => {
    onFinish(form.getFieldsValue(true) as WizardFormValues);
  }, [form, onFinish]);

  const isDataLoading =
    catalogLoading || eventsLoading || isInitialValuesLoading;
  const isSubmitting =
    campaignPending || templatePending || seriesPending || isSubmittingProp;

  const defaultTriggerMode = useMemo(
    () => defaultTriggerModeFor(eventGroups ?? []),
    [eventGroups],
  );

  const stepOneEventKey = useMemo(
    () =>
      triggerEventKeyFor({
        trigger_mode: watchedTriggerMode,
        steps: watchedSteps,
      }),
    [watchedTriggerMode, watchedSteps],
  );

  const backRoute = isTemplateMode
    ? NAVIGATION_ROUTES.DYNAMIC_BONUS_TEMPLATES
    : seriesId
      ? NAVIGATION_ROUTES.DYNAMIC_BONUS_SERIES_DETAIL.replace(
          ":seriesId",
          seriesId,
        )
      : NAVIGATION_ROUTES.DYNAMIC_BONUS_CAMPAIGNS;

  const editTransitions = isEditMode
    ? campaignTransitions()[editCampaign?.status ?? ""] ?? []
    : [];
  const isCreateFlow = !readOnly && !isEditMode && !isTemplateMode && !onSubmit;
  const showCreateSendTest =
    isCreateFlow && !isSeries && Boolean(draftTestSendEnabled);
  const showEditSendTest =
    Boolean(editCampaign) &&
    !seriesId &&
    !TEST_SEND_BLOCKED_STATUSES.has(editCampaign!.status);
  const showSendTest = showCreateSendTest || showEditSendTest;
  const canSendDraftTest = stepsAdvanceFields(
    watchedTriggerMode ?? "none",
    watchedSteps,
    catalog,
    watchedAffiliateReferralCode,
  ).every((field) =>
    fieldHasValue(
      field === "affiliate_id" ? watchedAffiliateId : form.getFieldValue(field),
    ),
  );

  const handleOpenSendTest = () => {
    if (editCampaign) {
      setDraftCampaign(null);
      setSendTestOpen(true);
      return;
    }
    const triggerMode: WizardTriggerMode =
      form.getFieldValue("trigger_mode") ?? "none";
    const steps: WizardStepFormValue[] = form.getFieldValue("steps") ?? [];
    form
      .validateFields(
        stepsAdvanceFields(
          triggerMode,
          steps,
          catalog,
          form.getFieldValue("affiliate_referral_code"),
        ),
      )
      .then(() => {
        const allValues = form.getFieldsValue(true) as WizardFormValues;
        setDraftCampaign(buildCampaignBody(allValues, resolveResultType));
        setSendTestOpen(true);
      })
      .catch(() => {});
  };

  // Create campaign/series: draft (+ template + activate for PAM campaigns).
  // Template create/edit: primary create/save (+ delete on edit).
  // Campaign edit/view: save / transitions / send test.
  // Series edit: host onSubmit without editCampaign chrome.
  const isHostOwnedSave =
    Boolean(onSubmit) && !readOnly && !isTemplateMode && !isEditMode;
  const isTemplateCreate = isTemplateMode && !onDelete && Boolean(onSubmit);
  const isTemplateEdit = isTemplateMode && Boolean(onDelete);
  const showHeaderActions =
    isCreateFlow ||
    isEditMode ||
    isViewMode ||
    isHostOwnedSave ||
    isTemplateCreate ||
    isTemplateEdit ||
    Boolean(onDelete);

  const primaryCreateLabel = isSeries
    ? AppStrings.PAM_DYNAMIC_BONUS_CREATE_SERIES
    : AppStrings.PAM_DYNAMIC_BONUS_ACTIVATE_CAMPAIGN;

  const headerTitle =
    title ??
    (isTemplateMode
      ? AppStrings.PAM_DYNAMIC_BONUS_CREATE_TEMPLATE
      : isSeries
        ? AppStrings.PAM_DYNAMIC_BONUS_CREATE_SERIES
        : isDisplayBanner
          ? AppStrings.PAM_DYNAMIC_BONUS_CREATE_DISPLAY_BANNER
          : AppStrings.CREATE_CAMPAIGN);
  const backAriaLabel = isTemplateMode
    ? AppStrings.PAM_DYNAMIC_BONUS_BACK_TO_TEMPLATES
    : seriesId
      ? AppStrings.PAM_DYNAMIC_BONUS_BACK_TO_SERIES
      : AppStrings.PAM_DYNAMIC_BONUS_BACK_TO_CAMPAIGNS;

  return (
    <>
      <FeatureFlagGuard feature="dynamicBonuses" mode="route">
        <div className={wizardStyles.page}>
          <CampaignBuilderHeader
            title={headerTitle}
            backAriaLabel={backAriaLabel}
            onBack={() => navigate(backRoute)}
            actions={
              showHeaderActions ? (
                <>
                  {isCreateFlow && (
                    <Button
                      intent="neutral"
                      variant="outline"
                      color="var(--grey-600, #434350)"
                      iconLeft={<BookOutlined />}
                      loading={isSubmitting}
                      onClick={handleSaveAsDraft}
                    >
                      {AppStrings.PAM_DYNAMIC_BONUS_SAVE_AS_DRAFT}
                    </Button>
                  )}
                  {isCreateFlow && !isSeries && (
                    <Button
                      intent="neutral"
                      variant="outline"
                      color="var(--grey-600, #434350)"
                      iconLeft={<BookOutlined />}
                      loading={isSubmitting}
                      onClick={handleSaveAsTemplate}
                    >
                      {AppStrings.PAM_DYNAMIC_BONUS_SAVE_AS_TEMPLATE}
                    </Button>
                  )}
                  {isCreateFlow && (
                    <Button
                      intent="primary"
                      iconLeft={<SendOutlined />}
                      loading={isSubmitting}
                      onClick={handlePrimarySubmit}
                    >
                      {primaryCreateLabel}
                    </Button>
                  )}
                  {isTemplateCreate && (
                    <Button
                      intent="primary"
                      loading={isSubmitting}
                      onClick={handlePrimarySubmit}
                    >
                      {submitLabel ?? AppStrings.PAM_DYNAMIC_BONUS_CREATE_TEMPLATE}
                    </Button>
                  )}
                  {isTemplateEdit && (
                    <Button
                      intent="primary"
                      loading={isSubmitting}
                      onClick={handlePrimarySubmit}
                    >
                      {submitLabel ?? AppStrings.PAM_DYNAMIC_BONUS_SAVE_TEMPLATE}
                    </Button>
                  )}
                  {isEditMode && (
                    <Button
                      intent="primary"
                      iconLeft={<SaveOutlined />}
                      loading={isSubmitting}
                      onClick={handlePrimarySubmit}
                    >
                      {submitLabel ?? AppStrings.PAM_DYNAMIC_BONUS_SAVE_CAMPAIGN}
                    </Button>
                  )}
                  {isHostOwnedSave && (
                    <Button
                      intent="primary"
                      iconLeft={<SaveOutlined />}
                      loading={isSubmitting}
                      onClick={handlePrimarySubmit}
                    >
                      {submitLabel ??
                        (isSeries
                          ? AppStrings.PAM_DYNAMIC_BONUS_SERIES_SAVE
                          : AppStrings.PAM_DYNAMIC_BONUS_SAVE_CAMPAIGN)}
                    </Button>
                  )}
                  {onDuplicate && (
                    <Button
                      intent="neutral"
                      variant="outline"
                      iconLeft={<CopyOutlined />}
                      onClick={onDuplicate}
                    >
                      {AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_ACTION_DUPLICATE}
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      intent="danger"
                      iconLeft={<DeleteOutlined />}
                      onClick={onDelete}
                    >
                      {AppStrings.PAM_DYNAMIC_BONUS_DELETE_ACTION}
                    </Button>
                  )}
                  {showSendTest && (
                    <Button
                      intent="neutral"
                      variant="outline"
                      color="var(--grey-600, #434350)"
                      disabled={showCreateSendTest && !canSendDraftTest}
                      onClick={handleOpenSendTest}
                    >
                      {AppStrings.PAM_DYNAMIC_BONUS_SEND_TEST}
                    </Button>
                  )}
                  {editTransitions.map(({ action, label, danger }) =>
                    danger ? (
                      <Popconfirm
                        key={action}
                        title={formatString(
                          AppStrings.PAM_DYNAMIC_BONUS_CAMPAIGN_TRANSITION_CONFIRM_TITLE,
                          { action: label },
                        )}
                        onConfirm={() => transitionMutation.mutate(action)}
                        okText={label}
                        cancelText={AppStrings.PAM_DYNAMIC_BONUS_DELETE_CANCEL}
                      >
                        <Button
                          intent="danger"
                          loading={transitionMutation.isPending}
                        >
                          {label}
                        </Button>
                      </Popconfirm>
                    ) : (
                      <Button
                        key={action}
                        intent="primary"
                        loading={transitionMutation.isPending}
                        onClick={() => transitionMutation.mutate(action)}
                      >
                        {label}
                      </Button>
                    ),
                  )}
                </>
              ) : undefined
            }
          />

          <div className={`${wizardStyles.content} ${styles.contentNoAside}`}>
            <div className={wizardStyles.wizardCard}>
              <div className={wizardStyles.stepPanel}>
                {initialValuesError ? (
                  <p className={styles.listError}>{initialValuesError}</p>
                ) : isDataLoading ? (
                  <Spin />
                ) : (
                  <Form
                    form={form}
                    onFinish={onFinish}
                    preserve
                    disabled={readOnly || rulesLocked}
                    // Creating / activating must only happen via an explicit
                    // header CTA — block Enter in single-line inputs.
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" &&
                        (event.target as HTMLElement).tagName === "INPUT"
                      ) {
                        event.preventDefault();
                      }
                    }}
                    className={styles.form}
                    initialValues={{
                      campaign_type: defaultCampaignType,
                      start_mode: "new",
                      schedule_mode:
                        initialCampaignType === SERIES_CAMPAIGN_TYPE
                          ? "never"
                          : DEFAULT_SCHEDULE_MODE,
                      priority: "medium",
                      audience_mode: "all",
                      trigger_mode:
                        initialCampaignType === SERIES_CAMPAIGN_TYPE
                          ? "campaign_start"
                          : defaultTriggerMode,
                      bet_targeted_preset: null,
                      date_range:
                        initialCampaignType === SERIES_CAMPAIGN_TYPE
                          ? [null, null]
                          : undefined,
                      steps:
                        initialCampaignType === SERIES_CAMPAIGN_TYPE
                          ? [{}, {}]
                          : [{}],
                      award_frequency: AWARD_FREQUENCY.ONE_TIME,
                      award_frequency_amount: DEFAULT_CUSTOM_FREQUENCY_AMOUNT,
                      award_frequency_unit: DEFAULT_CUSTOM_FREQUENCY_UNIT,
                      ...initialValues,
                    }}
                  >
                    {readOnly && seriesId && (
                      <Alert
                        style={{ marginBottom: 20 }}
                        type="warning"
                        showIcon
                        message={AppStrings.PAM_DYNAMIC_BONUS_SERIES_RUN_READONLY_NOTICE}
                      />
                    )}
                    <div className={styles.detailsStep}>
                      <div
                        className={readOnly ? styles.readOnlyFields : undefined}
                        ref={(element) => {
                          if (readOnly) {
                            element?.setAttribute("inert", "");
                          } else {
                            element?.removeAttribute("inert");
                          }
                        }}
                        aria-disabled={readOnly || undefined}
                      >
                        <section className={styles.detailsSection}>
                          <StartStep
                            form={form}
                            mode={mode}
                            defaultTriggerMode={defaultTriggerMode}
                            rulesLocked={rulesLocked}
                            readOnly={readOnly}
                            lockBrazeSource={brazeSourceLocked}
                            catalog={catalog}
                            showAudience={
                              !isSeries && !isDisplayBanner && !brazeSourceLocked
                            }
                            showFooter={false}
                          />
                        </section>
                        <section className={styles.detailsSection}>
                          <TriggerStepsStep
                            form={form}
                            catalog={catalog}
                            eventGroups={eventGroups ?? []}
                            stepOneEventKey={stepOneEventKey}
                            rulesLocked={rulesLocked}
                            readOnly={readOnly}
                            showFooter={false}
                          />
                          {isDisplayBanner && (
                            <DisplayBannerStep
                              form={form}
                              rulesLocked={rulesLocked}
                              readOnly={readOnly}
                            />
                          )}
                        </section>
                      </div>
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </div>

          {showSendTest && (
            <SendTestModal
              open={sendTestOpen}
              onClose={() => {
                setSendTestOpen(false);
                setDraftCampaign(null);
              }}
              campaignId={editCampaign?.campaignId}
              campaign={draftCampaign ?? undefined}
            />
          )}

          {isCreateFlow && !isSeries && (
            <ActivateCampaignReviewModal
              open={isReviewOpen}
              values={form.getFieldsValue(true) as WizardFormValues}
              eventLabel={displayNameFor(eventGroups ?? [], stepOneEventKey)}
              eventGroups={eventGroups ?? []}
              loading={isSubmitting}
              onClose={() => {
                if (!isSubmitting) setIsReviewOpen(false);
              }}
              onSaveAsDraft={handleReviewSaveAsDraft}
              onActivate={handleReviewActivate}
            />
          )}
        </div>
      </FeatureFlagGuard>
    </>
  );
};

export default CreateCampaignWizard;
